import { useEffect, useState, useCallback } from "react";
import { Building2, Ruler, Truck, Plus, Pencil, Trash2, Save, Gift, Coins, Share2, MessageCircle, LayoutTemplate, Bell, Send, ShieldCheck, RefreshCw, Images, ArrowUp, ArrowDown, Mail, Download } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Loader } from "@/components/common";
import { ImageUploader } from "@/components/common";
import Modal, { TextField, SelectField } from "@/components/Modal";
import { PageTitle, Panel } from "./AdminShell";
import { formatFCFA } from "@/lib/format";
import { toast } from "sonner";

const TABS = [
  { key: "company", label: "Entreprise", icon: Building2 },
  { key: "units", label: "Unités", icon: Ruler },
  { key: "vehicles", label: "Véhicules", icon: Truck },
  { key: "caisses", label: "Caisses", icon: Coins },
  { key: "footer", label: "Footer & Contact", icon: Share2 },
  { key: "home", label: "Accueil", icon: LayoutTemplate },
  { key: "banners", label: "Bannières", icon: Images },
  { key: "newsletter", label: "Newsletter", icon: Mail },
  { key: "notifications", label: "Notifications", icon: Bell },
  { key: "templates", label: "Modèles", icon: LayoutTemplate },
  { key: "referral", label: "Parrainage client", icon: Gift },
];

export default function AdminSettings() {
  const [tab, setTab] = useState("company");
  return (
    <div>
      <PageTitle>Paramètres</PageTitle>
      <div className="flex gap-2 mb-5 overflow-x-auto no-scrollbar">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`settings-tab-${t.key}`}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition ${tab === t.key ? "bg-[#C85A32] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-[#C85A32]/50"}`}>
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>
      {tab === "company" && <CompanyTab />}
      {tab === "units" && <UnitsTab />}
      {tab === "vehicles" && <VehiclesTab />}
      {tab === "caisses" && <CaissesTab />}
      {tab === "footer" && <FooterContactTab />}
      {tab === "home" && <HomeSectionsTab />}
      {tab === "banners" && <BannersTab />}
      {tab === "newsletter" && <NewsletterTab />}
      {tab === "notifications" && <NotificationsTab />}
      {tab === "templates" && <TemplatesTab />}
      {tab === "referral" && <ClientReferralTab />}
    </div>
  );
}

function CaissesTab() {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null); // {id?, name, description, active}
  const load = () => api.get("/admin/caisses").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  if (!rows) return <Loader />;
  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { name: form.name, description: form.description || "", active: form.active !== false };
      if (form.id) await api.put(`/admin/caisses/${form.id}`, body);
      else await api.post("/admin/caisses", body);
      toast.success("Caisse enregistrée"); setForm(null); load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const del = async (c) => {
    try { await api.delete(`/admin/caisses/${c.id}`); toast.success("Caisse supprimée"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const isDefault = (c) => ["physique", "electronique"].includes(c.key);
  return (
    <div className="max-w-2xl" data-testid="caisses-tab">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2"><Coins className="w-5 h-5 text-[#C85A32]" /><h2 className="font-display text-lg font-bold">Caisses</h2></div>
        <button onClick={() => setForm({ name: "", description: "", active: true })} className="bg-[#C85A32] text-white font-bold text-sm px-4 py-2 rounded-lg inline-flex items-center gap-1" data-testid="add-caisse-btn"><Plus className="w-4 h-4" /> Nouvelle caisse</button>
      </div>
      <div className="space-y-2">
        {rows.map((c) => (
          <Panel key={c.id} className="p-3 flex items-center gap-3" data-testid={`caisse-${c.id}`}>
            <div className="flex-1">
              <p className="font-semibold text-sm">{c.name} {!c.active && <span className="text-[10px] text-slate-400">(inactive)</span>}</p>
              {c.description && <p className="text-xs text-slate-500">{c.description}</p>}
            </div>
            <button onClick={() => setForm({ id: c.id, name: c.name, description: c.description, active: c.active })} className="p-2 rounded-lg border border-slate-200" data-testid={`caisse-edit-${c.id}`}><Pencil className="w-4 h-4 text-slate-600" /></button>
            {!isDefault(c) && <button onClick={() => del(c)} className="p-2 rounded-lg border border-red-200" data-testid={`caisse-del-${c.id}`}><Trash2 className="w-4 h-4 text-red-600" /></button>}
          </Panel>
        ))}
      </div>
      {form && (
        <Modal title={form.id ? "Modifier la caisse" : "Nouvelle caisse"} onClose={() => setForm(null)} testid="caisse-form">
          <form onSubmit={save} className="space-y-3">
            <TextField label="Nom de la caisse" value={form.name} onChange={(v) => setForm((s) => ({ ...s, name: v }))} required testid="caisse-name" />
            <TextField label="Description (facultatif)" value={form.description || ""} onChange={(v) => setForm((s) => ({ ...s, description: v }))} required={false} testid="caisse-desc" />
            <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
              <input type="checkbox" checked={form.active !== false} onChange={(e) => setForm((s) => ({ ...s, active: e.target.checked }))} data-testid="caisse-active" /> Caisse active
            </label>
            <button className="w-full bg-[#C85A32] text-white font-bold py-3 rounded-lg" data-testid="caisse-submit"><Save className="w-4 h-4 inline mr-1" /> Enregistrer</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

function FooterContactTab() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/site-settings").then((r) => setS(r.data)); }, []);
  if (!s) return <Loader />;
  const socials = s.social_links || [];
  const wa = s.whatsapp || { number: "", enabled: true, message: "" };
  const setWa = (k, v) => setS((x) => ({ ...x, whatsapp: { ...wa, [k]: v } }));
  const setSocial = (i, k, v) => setS((x) => { const arr = [...(x.social_links || [])]; arr[i] = { ...arr[i], [k]: v }; return { ...x, social_links: arr }; });
  const addSocial = () => setS((x) => ({ ...x, social_links: [...(x.social_links || []), { platform: "facebook", url: "" }] }));
  const rmSocial = (i) => setS((x) => ({ ...x, social_links: (x.social_links || []).filter((_, idx) => idx !== i) }));
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/site-settings", { copyright: s.copyright, social_links: socials.filter((l) => l.url), whatsapp: wa, cart_reminder_days: Number(s.cart_reminder_days) || 2, quote_reminder_days: Number(s.quote_reminder_days) || 3 });
      toast.success("Paramètres enregistrés");
    } catch (err) { toast.error(apiError(err)); }
  };
  const PLATFORMS = ["facebook", "instagram", "twitter", "youtube", "linkedin", "whatsapp", "tiktok", "other"];
  return (
    <form onSubmit={save} className="max-w-2xl space-y-5" data-testid="footer-contact-tab">
      <Panel className="p-5">
        <div className="flex items-center gap-2 mb-3"><Share2 className="w-5 h-5 text-[#C85A32]" /><h2 className="font-display text-lg font-bold">Réseaux sociaux</h2></div>
        <div className="space-y-2">
          {socials.map((l, i) => (
            <div key={i} className="flex gap-2 items-center" data-testid={`social-row-${i}`}>
              <select value={l.platform} onChange={(e) => setSocial(i, "platform", e.target.value)} className="border border-slate-300 rounded-lg px-2 py-2 text-sm" data-testid={`social-platform-${i}`}>
                {PLATFORMS.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
              <input value={l.url} onChange={(e) => setSocial(i, "url", e.target.value)} placeholder="https://..." className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid={`social-url-${i}`} />
              <button type="button" onClick={() => rmSocial(i)} className="p-2 rounded-lg border border-red-200" data-testid={`social-rm-${i}`}><Trash2 className="w-4 h-4 text-red-600" /></button>
            </div>
          ))}
        </div>
        <button type="button" onClick={addSocial} className="mt-3 text-sm font-semibold text-[#C85A32] inline-flex items-center gap-1" data-testid="social-add"><Plus className="w-4 h-4" /> Ajouter un réseau</button>
      </Panel>
      <Panel className="p-5">
        <div className="flex items-center gap-2 mb-3"><MessageCircle className="w-5 h-5 text-[#25D366]" /><h2 className="font-display text-lg font-bold">Bouton WhatsApp</h2></div>
        <label className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3">
          <input type="checkbox" checked={wa.enabled !== false} onChange={(e) => setWa("enabled", e.target.checked)} data-testid="wa-enabled" /> Afficher le bouton WhatsApp flottant
        </label>
        <TextField label="Numéro WhatsApp (format international)" value={wa.number || ""} onChange={(v) => setWa("number", v)} testid="wa-number" placeholder="+22871333346" />
        <TextField label="Message pré-rempli" value={wa.message || ""} onChange={(v) => setWa("message", v)} testid="wa-message" required={false} />
      </Panel>
      <Panel className="p-5">
        <h2 className="font-display text-lg font-bold mb-3">Copyright</h2>
        <TextField label="Texte du copyright (utilisez {year} pour l'année)" value={s.copyright || ""} onChange={(v) => setS((x) => ({ ...x, copyright: v }))} testid="copyright-text" />
      </Panel>
      <Panel className="p-5">
        <h2 className="font-display text-lg font-bold mb-3">Relances automatiques</h2>
        <p className="text-xs text-slate-500 mb-3">Délai avant l'envoi d'une relance au client (en jours).</p>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-sm font-semibold text-slate-700">Panier abandonné
            <input type="number" min="1" value={s.cart_reminder_days ?? 2} onChange={(e) => setS((x) => ({ ...x, cart_reminder_days: e.target.value }))} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="cart-reminder-days" />
          </label>
          <label className="text-sm font-semibold text-slate-700">Devis sans réponse
            <input type="number" min="1" value={s.quote_reminder_days ?? 3} onChange={(e) => setS((x) => ({ ...x, quote_reminder_days: e.target.value }))} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="quote-reminder-days" />
          </label>
        </div>
      </Panel>
      <Panel className="p-5">
        <h2 className="font-display text-lg font-bold mb-1">Email (Resend)</h2>
        <p className="text-xs text-slate-500 mb-3">Expéditeur : <strong>TOGOBATI</strong> · Reply-To : <strong>support@togobati.com</strong>. Envoyez-vous un email de test pour vérifier la configuration.</p>
        <button type="button" onClick={async () => { try { const { data } = await api.post("/admin/send-test-email"); toast.success(`Email de test envoyé à ${data.to}`); } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'envoi de l'email de test"); } }} className="bg-[#0F172A] text-white font-bold px-4 py-2.5 rounded-lg" data-testid="send-test-email">Envoyer un email de test</button>
      </Panel>
      <button className="bg-[#C85A32] text-white font-bold px-5 py-2.5 rounded-lg inline-flex items-center gap-2" data-testid="footer-contact-save"><Save className="w-4 h-4" /> Enregistrer</button>
    </form>
  );
}

function HomeSectionsTab() {
  const [home, setHome] = useState(null);
  useEffect(() => { api.get("/site-settings").then((r) => setHome(r.data.home || {})); }, []);
  if (!home) return <Loader />;
  const upd = (sec, k, v) => setHome((h) => ({ ...h, [sec]: { ...(h[sec] || {}), [k]: v } }));
  const save = async (e) => {
    e.preventDefault();
    try { await api.put("/admin/site-settings", { home }); toast.success("Accueil enregistré"); }
    catch (err) { toast.error(apiError(err)); }
  };
  const Sec = ({ id, label, withButton, withImage }) => {
    const s = home[id] || {};
    return (
      <Panel className="p-5 space-y-3" data-testid={`home-sec-${id}`}>
        <label className="flex items-center gap-2 font-display font-bold text-[#0F172A]">
          <input type="checkbox" checked={s.enabled !== false} onChange={(e) => upd(id, "enabled", e.target.checked)} data-testid={`home-${id}-enabled`} /> {label}
        </label>
        <TextField label="Titre" value={s.title || ""} onChange={(v) => upd(id, "title", v)} testid={`home-${id}-title`} />
        <label className="block"><span className="text-sm font-semibold text-slate-700 block mb-1.5">Texte</span>
          <textarea value={s.text || ""} onChange={(e) => upd(id, "text", e.target.value)} rows={3} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid={`home-${id}-text`} />
        </label>
        {withImage && <div><span className="text-sm font-semibold text-slate-700 block mb-1.5">Image</span>
          <ImageUploader value={s.image ? [s.image] : []} onChange={(arr) => upd(id, "image", arr[0] || "")} multiple={false} testid={`home-${id}-image`} />
        </div>}
        {withButton && <div className="grid grid-cols-2 gap-2">
          <TextField label="Libellé bouton" value={s.button_label || ""} onChange={(v) => upd(id, "button_label", v)} testid={`home-${id}-btnlabel`} required={false} />
          <TextField label="Lien bouton" value={s.button_link || ""} onChange={(v) => upd(id, "button_link", v)} testid={`home-${id}-btnlink`} required={false} />
        </div>}
      </Panel>
    );
  };
  return (
    <form onSubmit={save} className="max-w-2xl space-y-4" data-testid="home-sections-tab">
      <Sec id="communication" label="Section communication" withImage />
      <Sec id="influencer" label="Section recrutement influenceurs" withButton />
      <Sec id="project" label="Section « Votre projet »" withImage withButton />
      <button className="bg-[#C85A32] text-white font-bold px-5 py-2.5 rounded-lg inline-flex items-center gap-2" data-testid="home-save"><Save className="w-4 h-4" /> Enregistrer</button>
    </form>
  );
}

function ClientReferralTab() {
  const [f, setF] = useState(null);
  const [stats, setStats] = useState(null);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  useEffect(() => {
    api.get("/admin/client-referral/settings").then((r) => setF(r.data));
    api.get("/admin/client-referral/stats").then((r) => setStats(r.data));
  }, []);
  if (!f) return <Loader />;
  const save = async (e) => {
    e.preventDefault();
    try {
      await api.put("/admin/client-referral/settings", {
        enabled: f.enabled, referrer_reward: Number(f.referrer_reward), referee_reward: Number(f.referee_reward),
      });
      toast.success("Paramètres enregistrés");
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <div className="max-w-2xl space-y-4" data-testid="client-referral-tab">
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[["Filleuls", stats.total_referrals], ["Récompensés", stats.rewarded], ["En attente", stats.pending], ["Crédits versés", formatFCFA(stats.total_credited)]].map(([l, v]) => (
            <Panel key={l} className="p-3"><p className="text-lg font-display font-extrabold text-[#0F172A]">{v}</p><p className="text-xs text-slate-500">{l}</p></Panel>
          ))}
        </div>
      )}
      <Panel className="p-5">
        <div className="flex items-center gap-2 mb-4"><Gift className="w-5 h-5 text-[#C85A32]" /><h2 className="font-display text-lg font-bold">Programme « Parrainer & Gagner »</h2></div>
        <form onSubmit={save} className="space-y-3">
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <input type="checkbox" checked={f.enabled} onChange={(e) => set("enabled")(e.target.checked)} data-testid="cr-enabled" /> Programme actif
          </label>
          <TextField label="Crédit au parrain (FCFA) — à la 1re commande livrée du filleul" type="number" value={String(f.referrer_reward)} onChange={set("referrer_reward")} testid="cr-referrer" />
          <TextField label="Réduction du filleul (FCFA) — sur sa 1re commande" type="number" value={String(f.referee_reward)} onChange={set("referee_reward")} testid="cr-referee" />
          <button className="inline-flex items-center gap-2 bg-[#C85A32] text-white font-bold px-5 py-2.5 rounded-lg" data-testid="cr-save"><Save className="w-4 h-4" /> Enregistrer</button>
          <p className="text-xs text-slate-400">Le crédit du parrain est versé sur son portefeuille dès la 1re commande livrée de son filleul. La réduction du filleul s'applique automatiquement à sa première commande.</p>
        </form>
      </Panel>
    </div>
  );
}

function NotificationsTab() {
  const [cfg, setCfg] = useState(null);
  const [ch, setCh] = useState({ sms: false, whatsapp: false, email: true, push: true });
  const [savingCh, setSavingCh] = useState(false);
  const [sms, setSms] = useState({ to: "", body: "" });
  const [wa, setWa] = useState({ to: "", body: "" });
  const [otp, setOtp] = useState({ to: "", channel: "sms", code: "", started: false });
  const [busy, setBusy] = useState("");
  const [logs, setLogs] = useState([]);
  const [logFilter, setLogFilter] = useState("");

  const loadCfg = useCallback(() => api.get("/admin/notifications/config").then((r) => { setCfg(r.data.configured); setCh(r.data.channels); }), []);
  const loadLogs = useCallback(() => api.get("/admin/notifications/logs", { params: logFilter ? { channel: logFilter } : {} }).then((r) => setLogs(r.data)), [logFilter]);
  useEffect(() => { loadCfg(); }, [loadCfg]);
  useEffect(() => { loadLogs(); }, [loadLogs]);
  if (!cfg) return <Loader />;

  const saveCh = async () => {
    setSavingCh(true);
    try { await api.put("/admin/notifications/settings", ch); toast.success("Canaux mis à jour"); }
    catch (e) { toast.error(apiError(e)); } finally { setSavingCh(false); }
  };
  const run = async (key, fn) => { setBusy(key); try { await fn(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(""); loadLogs(); } };
  const doSms = () => run("sms", async () => { const { data } = await api.post("/admin/notifications/test-sms", sms); data.ok ? toast.success(`SMS envoyé à ${data.to}`) : toast.error(data.error || "Échec de l'envoi"); });
  const doWa = () => run("wa", async () => { const { data } = await api.post("/admin/notifications/test-whatsapp", wa); data.ok ? toast.success(`WhatsApp envoyé à ${data.to}`) : toast.error(data.error || "Échec de l'envoi"); });
  const doOtpStart = () => run("otp", async () => { const { data } = await api.post("/admin/notifications/test-otp", { to: otp.to, channel: otp.channel }); if (data.ok) { setOtp((s) => ({ ...s, started: true })); toast.success(`Code envoyé (${data.status}) à ${data.to}`); } else { toast.error(data.error || "Échec de l'envoi du code"); } });
  const doOtpCheck = () => run("otpv", async () => { const { data } = await api.post("/admin/notifications/verify-otp", { to: otp.to, code: otp.code }); if (!data.ok) { toast.error(data.error || "Erreur de vérification"); } else { data.valid ? toast.success("Code valide ✅") : toast.error("Code invalide ❌"); } });

  const Badge = ({ ok }) => <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${ok ? "bg-green-100 text-green-700" : "bg-slate-200 text-slate-500"}`}>{ok ? "Configuré" : "Non configuré"}</span>;
  const Toggle = ({ k, label, hint }) => (
    <label className="flex items-center gap-3 py-2 cursor-pointer" data-testid={`notif-toggle-${k}`}>
      <input type="checkbox" checked={!!ch[k]} onChange={(e) => setCh((s) => ({ ...s, [k]: e.target.checked }))} className="w-4 h-4" />
      <span className="flex-1"><span className="text-sm font-semibold text-slate-800">{label}</span>{hint && <span className="block text-xs text-slate-500">{hint}</span>}</span>
    </label>
  );
  const STATUS_COLOR = { error: "text-red-600 bg-red-50", skipped: "text-amber-600 bg-amber-50", sent: "text-green-600 bg-green-50", delivered: "text-green-700 bg-green-100", queued: "text-blue-600 bg-blue-50", pending: "text-blue-600 bg-blue-50", approved: "text-green-700 bg-green-100" };

  return (
    <div className="max-w-3xl space-y-4" data-testid="notifications-tab">
      <Panel className="p-5">
        <div className="flex items-center gap-2 mb-1"><Bell className="w-5 h-5 text-[#C85A32]" /><h2 className="font-display text-lg font-bold">Canaux de notification</h2></div>
        <p className="text-xs text-slate-500 mb-3">Choisissez les canaux utilisés pour les notifications automatiques (commandes, paiements, livraisons, devis…).</p>
        <div className="flex flex-wrap gap-2 mb-3 text-xs">
          <span className="inline-flex items-center gap-1">SMS <Badge ok={cfg.sms} /></span>
          <span className="inline-flex items-center gap-1">WhatsApp <Badge ok={cfg.whatsapp} /></span>
          <span className="inline-flex items-center gap-1">OTP (Verify) <Badge ok={cfg.otp} /></span>
        </div>
        <div className="divide-y divide-slate-100">
          <Toggle k="email" label="Email" hint="Notifications par email (Resend)" />
          <Toggle k="push" label="Push" hint="Notifications navigateur (Web Push)" />
          <Toggle k="sms" label="SMS" hint={cfg.sms ? "Envoi via Twilio" : "Ajoutez les secrets Twilio pour activer réellement"} />
          <Toggle k="whatsapp" label="WhatsApp" hint={cfg.whatsapp ? "Envoi via Twilio WhatsApp" : "Ajoutez TWILIO_WHATSAPP_NUMBER pour activer"} />
        </div>
        <button onClick={saveCh} disabled={savingCh} className="mt-3 bg-[#C85A32] text-white font-bold px-5 py-2.5 rounded-lg inline-flex items-center gap-2 disabled:opacity-50" data-testid="notif-save-channels"><Save className="w-4 h-4" /> Enregistrer les canaux</button>
      </Panel>

      <Panel className="p-5">
        <div className="flex items-center gap-2 mb-3"><Send className="w-5 h-5 text-[#0F172A]" /><h2 className="font-display text-lg font-bold">Tester un SMS</h2></div>
        <div className="space-y-2">
          <TextField label="Numéro destinataire" value={sms.to} onChange={(v) => setSms((s) => ({ ...s, to: v }))} testid="test-sms-to" placeholder="+228 90 00 00 00" />
          <TextField label="Message (facultatif)" value={sms.body} onChange={(v) => setSms((s) => ({ ...s, body: v }))} required={false} testid="test-sms-body" />
          <button onClick={doSms} disabled={busy === "sms" || !sms.to} className="bg-[#0F172A] text-white font-bold px-4 py-2.5 rounded-lg disabled:opacity-50" data-testid="test-sms-btn">{busy === "sms" ? "Envoi…" : "Envoyer le SMS"}</button>
        </div>
      </Panel>

      <Panel className="p-5">
        <div className="flex items-center gap-2 mb-3"><MessageCircle className="w-5 h-5 text-[#25D366]" /><h2 className="font-display text-lg font-bold">Tester un WhatsApp</h2></div>
        <div className="space-y-2">
          <TextField label="Numéro destinataire" value={wa.to} onChange={(v) => setWa((s) => ({ ...s, to: v }))} testid="test-wa-to" placeholder="+228 90 00 00 00" />
          <TextField label="Message (facultatif)" value={wa.body} onChange={(v) => setWa((s) => ({ ...s, body: v }))} required={false} testid="test-wa-body" />
          <button onClick={doWa} disabled={busy === "wa" || !wa.to} className="bg-[#25D366] text-white font-bold px-4 py-2.5 rounded-lg disabled:opacity-50" data-testid="test-wa-btn">{busy === "wa" ? "Envoi…" : "Envoyer le WhatsApp"}</button>
        </div>
      </Panel>

      <Panel className="p-5">
        <div className="flex items-center gap-2 mb-3"><ShieldCheck className="w-5 h-5 text-[#C85A32]" /><h2 className="font-display text-lg font-bold">Tester un code OTP (Verify)</h2></div>
        <div className="space-y-2">
          <TextField label="Numéro à vérifier" value={otp.to} onChange={(v) => setOtp((s) => ({ ...s, to: v }))} testid="test-otp-to" placeholder="+228 90 00 00 00" />
          <SelectField label="Canal" value={otp.channel} onChange={(v) => setOtp((s) => ({ ...s, channel: v }))} options={[{ value: "sms", label: "SMS" }, { value: "whatsapp", label: "WhatsApp" }]} testid="test-otp-channel" placeholder="Canal" />
          <button onClick={doOtpStart} disabled={busy === "otp" || !otp.to} className="bg-[#C85A32] text-white font-bold px-4 py-2.5 rounded-lg disabled:opacity-50" data-testid="test-otp-send-btn">{busy === "otp" ? "Envoi…" : "Envoyer le code"}</button>
          {otp.started && (
            <div className="flex items-end gap-2 pt-2">
              <div className="flex-1"><TextField label="Code reçu" value={otp.code} onChange={(v) => setOtp((s) => ({ ...s, code: v }))} testid="test-otp-code" placeholder="123456" /></div>
              <button onClick={doOtpCheck} disabled={busy === "otpv" || !otp.code} className="bg-[#0F172A] text-white font-bold px-4 py-2.5 rounded-lg disabled:opacity-50" data-testid="test-otp-verify-btn">{busy === "otpv" ? "…" : "Vérifier"}</button>
            </div>
          )}
        </div>
      </Panel>

      <Panel className="p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display text-lg font-bold">Journal des envois</h2>
          <div className="flex items-center gap-2">
            <select value={logFilter} onChange={(e) => setLogFilter(e.target.value)} className="border border-slate-300 rounded-lg px-2 py-1.5 text-sm" data-testid="notif-log-filter">
              <option value="">Tous</option><option value="sms">SMS</option><option value="whatsapp">WhatsApp</option><option value="otp">OTP</option>
            </select>
            <button onClick={loadLogs} className="p-2 rounded-lg border border-slate-200" data-testid="notif-log-refresh"><RefreshCw className="w-4 h-4 text-slate-600" /></button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="notif-log-table">
            <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100"><th className="py-2 pr-2">Date</th><th className="pr-2">Canal</th><th className="pr-2">Destinataire</th><th className="pr-2">Statut</th><th>Détail</th></tr></thead>
            <tbody>
              {logs.length === 0 && <tr><td colSpan={5} className="py-4 text-slate-400 text-center">Aucun envoi enregistré pour l'instant.</td></tr>}
              {logs.map((l) => (
                <tr key={l.id} className="border-b border-slate-50" data-testid={`notif-log-row-${l.id}`}>
                  <td className="py-2 pr-2 whitespace-nowrap text-xs text-slate-500">{new Date(l.created_at).toLocaleString("fr-FR")}</td>
                  <td className="pr-2 text-xs font-semibold">{l.channel}</td>
                  <td className="pr-2 text-xs font-num">{l.to}</td>
                  <td className="pr-2"><span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_COLOR[l.status] || "text-slate-600 bg-slate-100"}`}>{l.status}</span></td>
                  <td className="text-xs text-slate-500 max-w-[220px] truncate">{l.error || l.body}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}

function BannersTab() {
  const [banners, setBanners] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/site-settings").then((r) => setBanners(r.data.banners || [])); }, []);
  if (!banners) return <Loader />;
  const upd = (i, k, v) => setBanners((b) => b.map((x, idx) => idx === i ? { ...x, [k]: v } : x));
  const add = () => setBanners((b) => [...b, { id: Date.now().toString(), image: "", title: "", subtitle: "", link: "/recherche", start: "", end: "", enabled: true }]);
  const rm = (i) => setBanners((b) => b.filter((_, idx) => idx !== i));
  const move = (i, d) => setBanners((b) => { const n = [...b]; const j = i + d; if (j < 0 || j >= n.length) return n; [n[i], n[j]] = [n[j], n[i]]; return n; });
  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/site-settings", { banners }); toast.success("Bannières enregistrées"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-3xl space-y-4" data-testid="banners-tab">
      <Panel className="p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2"><Images className="w-5 h-5 text-[#C85A32]" /><h2 className="font-display text-lg font-bold">Bannières du carrousel d'accueil</h2></div>
          <button onClick={add} className="bg-[#C85A32] text-white font-bold text-sm px-4 py-2 rounded-lg inline-flex items-center gap-1" data-testid="add-banner-btn"><Plus className="w-4 h-4" /> Ajouter</button>
        </div>
        <p className="text-xs text-slate-500 mt-1">Si aucune bannière n'est active, l'accueil affiche automatiquement vos meilleures promotions.</p>
      </Panel>
      {banners.length === 0 && <Panel className="p-6 text-sm text-slate-400 text-center">Aucune bannière. Ajoutez-en pour piloter le carrousel principal.</Panel>}
      {banners.map((b, i) => (
        <Panel key={b.id || i} className="p-4 space-y-3" data-testid={`banner-${i}`}>
          <div className="flex items-center justify-between">
            <span className="text-sm font-bold text-slate-700">Bannière {i + 1}</span>
            <div className="flex items-center gap-1">
              <button onClick={() => move(i, -1)} className="p-1.5 rounded-lg border border-slate-200" data-testid={`banner-up-${i}`}><ArrowUp className="w-4 h-4 text-slate-600" /></button>
              <button onClick={() => move(i, 1)} className="p-1.5 rounded-lg border border-slate-200" data-testid={`banner-down-${i}`}><ArrowDown className="w-4 h-4 text-slate-600" /></button>
              <button onClick={() => rm(i)} className="p-1.5 rounded-lg border border-red-200" data-testid={`banner-rm-${i}`}><Trash2 className="w-4 h-4 text-red-600" /></button>
            </div>
          </div>
          <div><span className="text-sm font-semibold text-slate-700 block mb-1.5">Image</span>
            <ImageUploader value={b.image ? [b.image] : []} onChange={(arr) => upd(i, "image", arr[0] || "")} multiple={false} testid={`banner-image-${i}`} />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <TextField label="Titre" value={b.title || ""} onChange={(v) => upd(i, "title", v)} testid={`banner-title-${i}`} required={false} />
            <TextField label="Lien (ex : /recherche)" value={b.link || ""} onChange={(v) => upd(i, "link", v)} testid={`banner-link-${i}`} required={false} />
          </div>
          <TextField label="Sous-titre" value={b.subtitle || ""} onChange={(v) => upd(i, "subtitle", v)} testid={`banner-subtitle-${i}`} required={false} />
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block"><span className="text-sm font-semibold text-slate-700 block mb-1.5">Début de diffusion (facultatif)</span>
              <input type="datetime-local" value={b.start || ""} onChange={(e) => upd(i, "start", e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid={`banner-start-${i}`} />
            </label>
            <label className="block"><span className="text-sm font-semibold text-slate-700 block mb-1.5">Fin de diffusion (facultatif)</span>
              <input type="datetime-local" value={b.end || ""} onChange={(e) => upd(i, "end", e.target.value)} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid={`banner-end-${i}`} />
            </label>
          </div>
          <p className="text-[11px] text-slate-400">Laissez vide pour diffuser en permanence. La bannière n'apparaît que dans sa fenêtre de dates.</p>
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <input type="checkbox" checked={b.enabled !== false} onChange={(e) => upd(i, "enabled", e.target.checked)} data-testid={`banner-enabled-${i}`} /> Bannière active
          </label>
          <div data-testid={`banner-preview-${i}`}>
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-400 block mb-1.5">Aperçu en direct</span>
            <div className="relative h-32 sm:h-40 rounded-xl overflow-hidden bg-slate-800">
              {b.image
                ? <img src={b.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
                : <div className="absolute inset-0 flex items-center justify-center text-slate-500 text-xs">Ajoutez une image pour voir l'aperçu</div>}
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
              <div className="absolute inset-0 p-4 flex flex-col justify-center max-w-[70%]">
                {b.title && <p className="text-white font-display text-lg sm:text-2xl font-black leading-tight drop-shadow">{b.title}</p>}
                {b.subtitle && <p className="text-white/85 text-xs sm:text-sm mt-1 drop-shadow">{b.subtitle}</p>}
                <span className="mt-3 inline-flex w-fit items-center gap-1.5 bg-[#C85A32] text-white text-xs font-bold px-3 py-1.5 rounded-lg">J'en profite</span>
              </div>
            </div>
          </div>
        </Panel>
      ))}
      <button onClick={save} disabled={busy} className="bg-[#C85A32] text-white font-bold px-5 py-2.5 rounded-lg inline-flex items-center gap-2 disabled:opacity-50" data-testid="banners-save"><Save className="w-4 h-4" /> {busy ? "Enregistrement…" : "Enregistrer les bannières"}</button>
    </div>
  );
}

function NewsletterTab() {
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
  const [rows, setRows] = useState(null);
  const [camps, setCamps] = useState([]);
  const [camp, setCamp] = useState({ subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const loadCamps = useCallback(() => api.get("/admin/newsletter/campaigns").then((r) => setCamps(r.data)).catch(() => {}), []);
  useEffect(() => { api.get("/admin/newsletter").then((r) => setRows(r.data)).catch(() => setRows([])); loadCamps(); }, [loadCamps]);
  if (!rows) return <Loader />;
  const exportCsv = () => {
    const data = [["Email", "Date d'abonnement"], ...rows.map((r) => [r.email, r.subscribed_at])];
    const csv = data.map((r) => r.map((c) => `"${String(c || "").replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = "abonnes-newsletter.csv"; a.click();
  };
  const sendCampaign = async () => {
    if (!camp.subject.trim() || !camp.message.trim()) return toast.error("Objet et message requis");
    if (!window.confirm(`Envoyer cette campagne à ${rows.length} abonné(s) ?`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/newsletter/campaign", camp);
      toast.success(`Campagne lancée vers ${data.recipients} abonné(s)`);
      setCamp({ subject: "", message: "" });
      setTimeout(loadCamps, 1500);
    } catch (e) { toast.error(apiError(e)); } finally { setSending(false); }
  };
  return (
    <div className="max-w-3xl space-y-4" data-testid="newsletter-tab">
      <Panel className="p-5 space-y-3" data-testid="newsletter-campaign">
        <div className="flex items-center gap-2"><Send className="w-5 h-5 text-[#C85A32]" /><h2 className="font-display text-lg font-bold">Envoyer une campagne</h2></div>
        <p className="text-xs text-slate-500">L'email sera envoyé à vos {rows.length} abonné(s) via Resend, à la mise en forme TOGOBATI.</p>
        <TextField label="Objet" value={camp.subject} onChange={(v) => setCamp((s) => ({ ...s, subject: v }))} testid="campaign-subject" placeholder="Ex : -20% sur le ciment cette semaine" />
        <label className="block"><span className="text-sm font-semibold text-slate-700 block mb-1.5">Message</span>
          <textarea value={camp.message} onChange={(e) => setCamp((s) => ({ ...s, message: e.target.value }))} rows={5} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="campaign-message" placeholder="Rédigez votre message…" />
        </label>
        <button onClick={sendCampaign} disabled={sending || rows.length === 0} className="bg-[#C85A32] text-white font-bold px-5 py-2.5 rounded-lg inline-flex items-center gap-2 disabled:opacity-50" data-testid="campaign-send"><Send className="w-4 h-4" /> {sending ? "Envoi…" : `Envoyer à ${rows.length} abonné(s)`}</button>
      </Panel>

      {camps.length > 0 && (
        <Panel className="p-0 overflow-hidden" data-testid="campaign-history">
          <div className="px-4 py-3 border-b border-slate-100">
            <h3 className="font-display font-bold text-sm text-[#0F172A]">Historique des campagnes</h3>
            <p className="text-[11px] text-slate-400 mt-0.5">Taux d'ouverture & d'échec mis à jour via les webhooks Resend (voir l'aide ci-dessous).</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]" data-testid="campaign-history-table">
              <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100">
                <th className="px-4 py-2.5">Date</th><th className="px-4 py-2.5">Objet</th>
                <th className="px-4 py-2.5 text-right">Envoyés</th><th className="px-4 py-2.5 text-right">Délivrés</th>
                <th className="px-4 py-2.5 text-right">Ouverts</th><th className="px-4 py-2.5 text-right">Échecs</th>
              </tr></thead>
              <tbody>
                {camps.map((c, i) => {
                  const sent = c.sent || 0;
                  const rate = (n) => sent ? ` (${Math.round((n / sent) * 100)}%)` : "";
                  return (
                    <tr key={c.id || i} className="border-b border-slate-50" data-testid={`campaign-row-${i}`}>
                      <td className="px-4 py-2.5 text-xs text-slate-500 whitespace-nowrap">{c.created_at ? new Date(c.created_at).toLocaleString("fr-FR") : "—"}</td>
                      <td className="px-4 py-2.5 font-semibold text-slate-700">{c.subject}</td>
                      <td className="px-4 py-2.5 text-right font-num text-slate-600">{sent}</td>
                      <td className="px-4 py-2.5 text-right font-num text-slate-600">{c.delivered || 0}<span className="text-[10px] text-slate-400">{rate(c.delivered || 0)}</span></td>
                      <td className="px-4 py-2.5 text-right font-num text-green-700 font-bold">{c.opened || 0}<span className="text-[10px] text-slate-400 font-normal">{rate(c.opened || 0)}</span></td>
                      <td className="px-4 py-2.5 text-right font-num text-red-600">{(c.bounced || 0) + (c.complained || 0)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 bg-slate-50 border-t border-slate-100 text-[11px] text-slate-500" data-testid="resend-webhook-hint">
            <span className="font-semibold text-slate-600">Configuration Resend :</span> ajoutez un webhook pointant vers <code className="bg-white px-1.5 py-0.5 rounded border border-slate-200 break-all">{`${BACKEND_URL}/api/webhooks/resend`}</code> (événements <em>delivered, opened, bounced, complained</em>).
          </div>
        </Panel>
      )}
      <Panel className="p-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2"><Mail className="w-5 h-5 text-[#C85A32]" /><h2 className="font-display text-lg font-bold">Abonnés newsletter <span className="text-slate-400 font-normal">({rows.length})</span></h2></div>
          <button onClick={exportCsv} disabled={!rows.length} className="inline-flex items-center gap-1.5 bg-[#0F172A] text-white text-sm font-bold px-3 py-2 rounded-lg hover:bg-slate-800 disabled:opacity-50" data-testid="newsletter-export"><Download className="w-4 h-4" /> Exporter CSV</button>
        </div>
      </Panel>
      <Panel className="p-0 overflow-hidden">
        <table className="w-full text-sm" data-testid="newsletter-table">
          <thead><tr className="text-left text-xs text-slate-400 border-b border-slate-100"><th className="px-4 py-3">Email</th><th className="px-4 py-3">Date d'abonnement</th></tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={2} className="px-4 py-6 text-center text-slate-400">Aucun abonné pour l'instant.</td></tr>}
            {rows.map((r, i) => (
              <tr key={i} className="border-b border-slate-50" data-testid={`newsletter-row-${i}`}>
                <td className="px-4 py-2.5 font-semibold text-slate-700">{r.email}</td>
                <td className="px-4 py-2.5 text-slate-500 text-xs">{r.subscribed_at ? new Date(r.subscribed_at).toLocaleString("fr-FR") : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}

function TemplatesTab() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/admin/notifications/templates").then((r) => setRows(r.data)); }, []);
  if (!rows) return <Loader />;
  const upd = (key, field, v) => setRows((rs) => rs.map((r) => r.key === key ? { ...r, [field]: v } : r));
  const save = async () => {
    setBusy(true);
    try {
      const body = {};
      rows.forEach((r) => { body[r.key] = { title: r.custom_title || "", message: r.custom_message || "" }; });
      await api.put("/admin/notifications/templates", body);
      toast.success("Modèles enregistrés");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="max-w-3xl space-y-4" data-testid="templates-tab">
      <Panel className="p-5">
        <div className="flex items-center gap-2 mb-1"><LayoutTemplate className="w-5 h-5 text-[#C85A32]" /><h2 className="font-display text-lg font-bold">Modèles de messages</h2></div>
        <p className="text-xs text-slate-500">Personnalisez le texte de chaque notification. Laissez vide pour utiliser le texte par défaut. Utilisez les variables entre accolades (ex : <code className="bg-slate-100 px-1 rounded">{"{order_number}"}</code>).</p>
      </Panel>
      {rows.map((r) => (
        <Panel key={r.key} className="p-5 space-y-3" data-testid={`template-${r.key}`}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-display font-bold text-[#0F172A]">{r.label}</h3>
            <div className="flex flex-wrap gap-1">
              {r.vars.map((v) => <span key={v} className="text-[10px] font-mono bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{`{${v}}`}</span>)}
            </div>
          </div>
          <TextField label="Titre" value={r.custom_title || ""} onChange={(v) => upd(r.key, "custom_title", v)} required={false} testid={`template-${r.key}-title`} placeholder={r.title} />
          <label className="block"><span className="text-sm font-semibold text-slate-700 block mb-1.5">Message</span>
            <textarea value={r.custom_message || ""} onChange={(e) => upd(r.key, "custom_message", e.target.value)} rows={2} placeholder={r.message} className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid={`template-${r.key}-message`} />
          </label>
        </Panel>
      ))}
      <button onClick={save} disabled={busy} className="bg-[#C85A32] text-white font-bold px-5 py-2.5 rounded-lg inline-flex items-center gap-2 disabled:opacity-50" data-testid="templates-save"><Save className="w-4 h-4" /> {busy ? "Enregistrement…" : "Enregistrer les modèles"}</button>
    </div>
  );
}

function CompanyTab() {
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  useEffect(() => { api.get("/settings/company").then((r) => setF(r.data)); }, []);
  if (!f) return <Loader />;
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { id, updated_at, ...body } = f;
      await api.put("/admin/settings/company", body);
      toast.success("Informations enregistrées");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <Panel className="p-5 max-w-2xl">
      <form onSubmit={save} className="space-y-4" data-testid="company-form">
        <div>
          <span className="text-sm font-semibold text-slate-700">Logo</span>
          <div className="mt-1">
            <ImageUploader value={f.logo ? [f.logo] : []} onChange={(urls) => set("logo")(urls[0] || "")} multiple={false} label="Téléverser le logo" testid="company-logo" />
          </div>
        </div>
        <TextField label="Raison sociale" value={f.name || ""} onChange={set("name")} testid="company-name" />
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="RCCM" value={f.rccm || ""} onChange={set("rccm")} required={false} testid="company-rccm" />
          <TextField label="NIF" value={f.nif || ""} onChange={set("nif")} required={false} testid="company-nif" />
        </div>
        <TextField label="Adresse" value={f.address || ""} onChange={set("address")} required={false} testid="company-address" />
        <div className="grid sm:grid-cols-2 gap-3">
          <TextField label="Téléphone" value={f.phone || ""} onChange={set("phone")} required={false} testid="company-phone" />
          <TextField label="Email" type="email" value={f.email || ""} onChange={set("email")} required={false} testid="company-email" />
        </div>
        <TextField label="Autres informations légales" value={f.legal || ""} onChange={set("legal")} required={false} testid="company-legal" />
        <button disabled={busy} className="inline-flex items-center gap-2 bg-[#C85A32] text-white font-bold px-5 py-2.5 rounded-lg disabled:opacity-50" data-testid="company-save">
          <Save className="w-4 h-4" /> {busy ? "Enregistrement..." : "Enregistrer"}
        </button>
        <p className="text-xs text-slate-400">Ces informations apparaîtront automatiquement sur les documents commerciaux (bordereaux, etc.).</p>
      </form>
    </Panel>
  );
}

function UnitsTab() {
  const [units, setUnits] = useState(null);
  const [edit, setEdit] = useState(null); // null | {} (new) | unit (edit)
  const load = useCallback(() => api.get("/units").then((r) => setUnits(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!units) return <Loader />;
  const del = async (id) => {
    if (!window.confirm("Supprimer cette unité ?")) return;
    try { await api.delete(`/admin/units/${id}`); toast.success("Unité supprimée"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <div className="max-w-2xl">
      <div className="flex justify-end mb-3">
        <button onClick={() => setEdit({})} className="inline-flex items-center gap-1 bg-[#C85A32] text-white font-bold text-sm px-4 py-2 rounded-lg" data-testid="add-unit-btn"><Plus className="w-4 h-4" /> Nouvelle unité</button>
      </div>
      <Panel className="divide-y divide-slate-100">
        {units.length === 0 && <p className="p-5 text-sm text-slate-400">Aucune unité. Ajoutez-en une (ex : sac, m², kg, pièce).</p>}
        {units.map((u) => (
          <div key={u.id} className="flex items-center gap-3 px-4 py-3" data-testid={`unit-row-${u.id}`}>
            <Ruler className="w-4 h-4 text-slate-400" />
            <span className="font-semibold text-slate-800">{u.name}</span>
            {u.symbol && <span className="text-xs text-slate-400">({u.symbol})</span>}
            <div className="ml-auto flex gap-1">
              <button onClick={() => setEdit(u)} className="p-2 text-slate-500 hover:text-[#C85A32]" data-testid={`unit-edit-${u.id}`}><Pencil className="w-4 h-4" /></button>
              <button onClick={() => del(u.id)} className="p-2 text-slate-500 hover:text-red-600" data-testid={`unit-del-${u.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
      </Panel>
      {edit && <UnitForm unit={edit} onClose={() => setEdit(null)} onDone={load} />}
    </div>
  );
}

function UnitForm({ unit, onClose, onDone }) {
  const isEdit = !!unit.id;
  const [f, setF] = useState({ name: unit.name || "", symbol: unit.symbol || "" });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (isEdit) await api.put(`/admin/units/${unit.id}`, f);
      else await api.post("/admin/units", f);
      toast.success(isEdit ? "Unité modifiée" : "Unité ajoutée"); onDone(); onClose();
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Modal title={isEdit ? "Modifier l'unité" : "Nouvelle unité"} onClose={onClose} testid="unit-form">
      <form onSubmit={submit} className="space-y-3">
        <TextField label="Nom" value={f.name} onChange={set("name")} testid="uf-name" placeholder="ex : Sac de 50kg" />
        <TextField label="Symbole (facultatif)" value={f.symbol} onChange={set("symbol")} required={false} testid="uf-symbol" placeholder="ex : sac, m², kg" />
        <button className="w-full bg-[#C85A32] text-white font-bold py-3 rounded-lg" data-testid="uf-submit">Enregistrer</button>
      </form>
    </Modal>
  );
}

const VEHICLE_STATUS = [
  { value: "actif", label: "Actif" },
  { value: "maintenance", label: "En maintenance" },
  { value: "inactif", label: "Inactif" },
];
const STATUS_STYLE = { actif: "bg-green-100 text-green-700", maintenance: "bg-amber-100 text-amber-700", inactif: "bg-slate-200 text-slate-600" };

function VehiclesTab() {
  const [vehicles, setVehicles] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get("/admin/vehicles").then((r) => setVehicles(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!vehicles) return <Loader />;
  const del = async (id) => {
    if (!window.confirm("Supprimer ce véhicule ?")) return;
    try { await api.delete(`/admin/vehicles/${id}`); toast.success("Véhicule supprimé"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  return (
    <div>
      <div className="flex justify-end mb-3">
        <button onClick={() => setEdit({})} className="inline-flex items-center gap-1 bg-[#C85A32] text-white font-bold text-sm px-4 py-2 rounded-lg" data-testid="add-vehicle-btn"><Plus className="w-4 h-4" /> Nouveau véhicule</button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {vehicles.length === 0 && <p className="text-sm text-slate-400">Aucun véhicule enregistré. Les véhicules créés ici seront disponibles lors de l'affectation des livraisons.</p>}
        {vehicles.map((v) => (
          <Panel key={v.id} className="p-4" data-testid={`vehicle-card-${v.id}`}>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#FDF3EE] flex items-center justify-center shrink-0"><Truck className="w-5 h-5 text-[#C85A32]" /></div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-800 truncate">{v.name}</p>
                <p className="text-xs font-num text-slate-500">{v.plate}</p>
                {v.info && <p className="text-xs text-slate-400 mt-0.5 line-clamp-2">{v.info}</p>}
                <span className={`inline-block mt-2 text-[10px] font-semibold uppercase px-2 py-0.5 rounded-full ${STATUS_STYLE[v.status] || STATUS_STYLE.inactif}`}>{VEHICLE_STATUS.find((s) => s.value === v.status)?.label || v.status}</span>
              </div>
            </div>
            <div className="flex justify-end gap-1 mt-2">
              <button onClick={() => setEdit(v)} className="p-2 text-slate-500 hover:text-[#C85A32]" data-testid={`vehicle-edit-${v.id}`}><Pencil className="w-4 h-4" /></button>
              <button onClick={() => del(v.id)} className="p-2 text-slate-500 hover:text-red-600" data-testid={`vehicle-del-${v.id}`}><Trash2 className="w-4 h-4" /></button>
            </div>
          </Panel>
        ))}
      </div>
      {edit && <VehicleForm vehicle={edit} onClose={() => setEdit(null)} onDone={load} />}
    </div>
  );
}

function VehicleForm({ vehicle, onClose, onDone }) {
  const isEdit = !!vehicle.id;
  const [f, setF] = useState({ name: vehicle.name || "", plate: vehicle.plate || "", info: vehicle.info || "", status: vehicle.status || "actif" });
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    try {
      if (isEdit) await api.put(`/admin/vehicles/${vehicle.id}`, f);
      else await api.post("/admin/vehicles", f);
      toast.success(isEdit ? "Véhicule modifié" : "Véhicule ajouté"); onDone(); onClose();
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Modal title={isEdit ? "Modifier le véhicule" : "Nouveau véhicule"} onClose={onClose} testid="vehicle-form">
      <form onSubmit={submit} className="space-y-3">
        <TextField label="Désignation" value={f.name} onChange={set("name")} testid="vf-name" placeholder="ex : Camion benne Isuzu" />
        <TextField label="Immatriculation" value={f.plate} onChange={set("plate")} testid="vf-plate" placeholder="ex : TG-1234-AB" />
        <TextField label="Informations (facultatif)" value={f.info} onChange={set("info")} required={false} testid="vf-info" placeholder="Capacité, marque, année..." />
        <SelectField label="Statut" value={f.status} onChange={set("status")} options={VEHICLE_STATUS} testid="vf-status" placeholder="Statut" />
        <button className="w-full bg-[#C85A32] text-white font-bold py-3 rounded-lg" data-testid="vf-submit">Enregistrer</button>
      </form>
    </Modal>
  );
}
